import { useDialog } from '@wui/common-library'
import { computed, ComputedRef, Ref, watch } from 'vue'

import { SuccessDialog } from '@/shared/components/dialogs'
import { useLocalization } from '@/shared/composables/localization'
import { eventBus } from '@/shared/services/jssip/event-bus'
import { Room } from '@/shared/types/store'

import { initConferenceRecordingTimer } from './use-conference-recording-timer'

const notifiedConferenceRecordingRooms = new Set<string>()

export const useConferenceRecordingNotificationState = () => {
  const hasBeenNotified = (roomKey: string) => notifiedConferenceRecordingRooms.has(roomKey)
  const markNotified = (roomKey: string) => {
    notifiedConferenceRecordingRooms.add(roomKey)
  }
  const clearNotified = (roomKey: string) => {
    notifiedConferenceRecordingRooms.delete(roomKey)
  }
  const reset = () => {
    notifiedConferenceRecordingRooms.clear()
  }

  return {
    hasBeenNotified,
    markNotified,
    clearNotified,
    reset,
  }
}

eventBus.on('callEndedInRoom', (roomPServed: string) => {
  notifiedConferenceRecordingRooms.delete(roomPServed)
})

type RoomSource = Ref<Room> | ComputedRef<Room>

export const useConferenceRecordingNotification = (room: RoomSource) => {
  initConferenceRecordingTimer()

  const { showDialog } = useDialog()
  const { t } = useLocalization()
  const { hasBeenNotified, markNotified, clearNotified } = useConferenceRecordingNotificationState()

  const isRecordingStarted = computed(() => room.value.isRecording)
  const roomPServedUser = computed(() => room.value.pServedUser)

  const showRecordingStartedDialogIfNeeded = () => {
    const roomKey = roomPServedUser.value

    if (!roomKey || !isRecordingStarted.value || hasBeenNotified(roomKey)) {
      return
    }

    markNotified(roomKey)
    showDialog(SuccessDialog, {
      information: t('common.callRecordingStartedMessage'),
      description: t('common.callRecordingStartedDescription'),
      hasOverlay: true,
      needToCloseOnTimeout: false,
    })
  }

  watch(isRecordingStarted, (isRecording) => {
    if (isRecording) {
      showRecordingStartedDialogIfNeeded()
      return
    }

    const roomKey = roomPServedUser.value
    if (roomKey) {
      clearNotified(roomKey)
    }
  }, { immediate: true })
}
